import React from "react";
import ProjectListMainSpace from "./projectlist/ProjectListMainSpace";
import ProjectDetailMainSpace from "./projectdetail/ProjectDetailMainSpace";
import { IMessageBoxParams } from "../../common/MessageBox";
import { IConfirmDialogParams } from "../../common/ConfirmDialog";
import { getProjects, IProjectRow } from "../../webapi/service/getProjects";
import AuthService from "../../common/AuthService";
import { Projects, ProjectDetail } from "../../common/constants";

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * 通知のuri
   */
  uri?: string;
  /**
   * 同一メインスペースの場合でもプロジェクト一覧を表示する場合はtrue
   */
  forceLoad?: boolean;
  /**
   * 通知から表示された場合に表示されたことを通知するために呼び出します
   */
  onFromNoticeLoaded: () => void;
  /**
   * プロジェクト一覧画面を強制的に表示する場合に呼び出します
   */
  onForceLoaded: () => void;
  /**
   * ローディング中の場合はtrue
   */
  isLoading: boolean;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   * @param onCloseMessage - メッセージBoxを閉じる場合に呼び出します
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined,
    onCloseMessage?: () => void
  ) => void;
  /**
   * 非操作タイムアウトをリセットします
   */
  resetUnoperatedTimeout: () => void;
}

interface IState {
  /**
   * 表示する画面。
   * Projects.PROJECT_LIST | Projects.PROJECT_DETAIL
   */
  mode: number | undefined;
  /**
   * プロジェクト情報
   */
  projectInfo: IProjectRow | undefined;
  /**
   * 初期表示する詳細画面。
   * undefined | ProjectDetail.FILES | ProjectDetail.RESULT_TEST_MODEL | ProjectDetail.RESULT_PROFILE
   */
  detail: number | undefined;
}

class ProjectMainSpace extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = { mode: undefined, projectInfo: undefined, detail: undefined };

    this.handleShowProjectList = this.handleShowProjectList.bind(this);
    this.handleShowFileList = this.handleShowFileList.bind(this);
    this.handleSelectProject = this.handleSelectProject.bind(this);
  }

  componentDidMount(): void {
    const { uri } = this.props;
    if (uri) {
      this.fromNotice(uri);
    } else {
      this.setState({
        mode: Projects.PROJECT_LIST,
        detail: ProjectDetail.FILES,
      });
    }
  }

  componentDidUpdate(prevProps: IProps): void {
    const { uri, forceLoad, onForceLoaded } = this.props;
    if (prevProps.uri !== uri) {
      if (uri) {
        this.fromNotice(uri);
      }
    } else if (
      prevProps.forceLoad !== forceLoad &&
      typeof forceLoad !== "undefined" &&
      forceLoad === true
    ) {
      this.setState({ mode: Projects.PROJECT_LIST });
      onForceLoaded();
    }
  }

  fromNotice(uri: string): void {
    const { authService, onFromNoticeLoaded, onCommunicationError } =
      this.props;
    const words = uri.split("/");
    const projectid = words[2];
    let detail: number | undefined = undefined;
    if (words.length === 4 && words[3] === "simulator") {
      detail = ProjectDetail.LIST_SIMULATOR;
    } else if (words.length === 4 && words[3] === "simeval") {
      detail = ProjectDetail.RESULT_SIMULATION_SIMULATOR;
    } else if (words.length === 4 && words[3] === "fkdppeval") {
      detail = ProjectDetail.EVALUATE_MODEL;
    } else if (words.length === 4 && words[3] === "fkdppsim") {
      detail = ProjectDetail.SIMULATE_MODEL;
    } else if (words[1] === "projects") {
      detail = ProjectDetail.FILES;
    }
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        this.handleShowFileList(rows[0], detail);
        onFromNoticeLoaded();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleShowProjectList(): void {
    this.props.resetUnoperatedTimeout();
    this.setState({ mode: Projects.PROJECT_LIST });
  }

  handleShowFileList(row: IProjectRow, detail?: number): void {
    this.props.resetUnoperatedTimeout();
    this.setState({
      mode: Projects.PROJECT_DETAIL,
      projectInfo: row,
      detail: detail ? detail : ProjectDetail.FILES,
    });
  }

  handleSelectProject(projectid: string) {
    const { authService, onCommunicationError } = this.props;
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        this.handleShowFileList(rows[0]);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  getMainSpace(): JSX.Element {
    let mainspace = <div />;
    switch (this.state.mode) {
      case Projects.PROJECT_LIST:
        mainspace = (
          <ProjectListMainSpace
            authService={this.props.authService}
            onLoading={this.props.onLoading}
            showMessage={this.props.showMessage}
            showConfirm={this.props.showConfirm}
            onCommunicationError={this.props.onCommunicationError}
            onSelect={this.handleSelectProject}
            resetUnoperatedTimeout={this.props.resetUnoperatedTimeout}
          />
        );
        break;
      case Projects.PROJECT_DETAIL:
        if (this.state.projectInfo) {
          mainspace = (
            <ProjectDetailMainSpace
              authService={this.props.authService}
              mainSpaceWidth={this.props.mainSpaceWidth}
              projectInfo={this.state.projectInfo}
              mode={this.state.detail}
              isLoading={this.props.isLoading}
              onLoading={this.props.onLoading}
              showMessage={this.props.showMessage}
              showConfirm={this.props.showConfirm}
              onCommunicationError={this.props.onCommunicationError}
              onBack={this.handleShowProjectList}
              resetUnoperatedTimeout={this.props.resetUnoperatedTimeout}
            />
          );
        }
        break;
    }
    return mainspace;
  }

  render(): JSX.Element {
    const mainspace = this.getMainSpace();
    return mainspace;
  }
}

export default ProjectMainSpace;
